import { computed } from "vue";
import { useI18n } from "vue-i18n";
import {
    SplitWithImage,
    type SplitWithImageProps,
    stats1,
    type Stats1Props,
    stats2,
    type Stats2Props,
    stats3,
    type Stats3Props,
    stats4,
    type Stats4Props,
    stepped,
    type SteppedProps,
    timeline,
    type TimelineProps,
} from "@/views/blocks/stats/stats";
import {
    arrayField,
    backgroundImageField,
    createBlock,
    descriptiveImageField,
    headingField,
    htmlField,
} from "./blockBuilder";
import type { BlockDef } from "./block.interfaces";
import { useDefaultColors } from "./useDefaultColors";
import { useFieldConfig } from "./useFieldConfig";
import { getStockPhotoUrl } from "@/apps/page/services/page.service";

export function useStats() {
    const { t } = useI18n();
    const { colorDefaults } = useDefaultColors();
    const { translations } = useFieldConfig();

    const stats1Block = computed<BlockDef>(() =>
        createBlock<Stats1Props>({
            typeId: "stats1",
            type: "stats",
            name: t("page.blocks.stats.stats1"),
            component: stats1,
        })
            .withArrayField(
                "stats",
                arrayField(
                    translations.value.stat,
                    "stats",
                    {
                        statTitle: { label: translations.value.title },
                        statDesc: { label: translations.value.description },
                    },
                    [
                        {
                            statTitle: "100+",
                            statDesc: "More clients",
                        },
                        {
                            statTitle: "50+",
                            statDesc: "More projects",
                        },
                        {
                            statTitle: "1+",
                            statDesc: "More years",
                        },
                    ],
                    {
                        statTitle: "New stat",
                        statDesc: "New description",
                    }
                )
            )
            .withStyleFields([colorDefaults.value.background, colorDefaults.value.text])
            .build()
    );

    const stats2Block = computed<BlockDef>(() =>
        createBlock<Stats2Props>({
            typeId: "stats2",
            type: "stats",
            name: t("page.blocks.stats.stats2"),
            component: stats2,
        })
            .withField(
                "title",
                headingField(translations.value.title, "By the numbers", "h2", {
                    text: translations.value.text,
                    tag: translations.value.tag,
                })
            )
            .withField(
                "subtitle",
                htmlField(
                    translations.value.subtitle,
                    "We're proud of our achievements",
                    translations.value.text
                )
            )
            .withArrayField(
                "stats",
                arrayField(
                    translations.value.stat,
                    "stats",
                    {
                        statTitle: { label: translations.value.title },
                        statDesc: { label: translations.value.description },
                    },
                    [
                        {
                            statTitle: "8K+",
                            statDesc: "Users",
                        },
                        {
                            statTitle: "25K+",
                            statDesc: "Downloads",
                        },
                        {
                            statTitle: "98%",
                            statDesc: "Satisfaction",
                        },
                        {
                            statTitle: "12M+",
                            statDesc: "Budget",
                        },
                    ],
                    {
                        statTitle: "New stat",
                        statDesc: "New description",
                    }
                )
            )
            .withStyleFields([colorDefaults.value.background, colorDefaults.value.text])
            .build()
    );

    const stats3Block = computed<BlockDef>(() =>
        createBlock<Stats3Props>({
            typeId: "stats3",
            type: "stats",
            name: t("page.blocks.stats.stats3"),
            component: stats3,
        })
            .withField(
                "title",
                headingField(translations.value.title, "Trusted by companies worldwide", "h2", {
                    text: translations.value.text,
                    tag: translations.value.tag,
                })
            )
            .withField(
                "subtitle",
                htmlField(
                    translations.value.subtitle,
                    "We're proud to help organizations transform their businesses",
                    translations.value.text
                )
            )
            .withArrayField(
                "stats",
                arrayField(
                    translations.value.stat,
                    "stats",
                    {
                        statTitle: { label: translations.value.title },
                        statDesc: { label: translations.value.description },
                    },
                    [
                        {
                            statTitle: "99.9%",
                            statDesc: "Uptime guarantee",
                        },
                        {
                            statTitle: "24/7",
                            statDesc: "Support",
                        },
                        {
                            statTitle: "100K+",
                            statDesc: "Transactions",
                        },
                    ],
                    {
                        statTitle: "New stat",
                        statDesc: "New description",
                    }
                )
            )
            .withStyleFields([colorDefaults.value.background, colorDefaults.value.text])
            .build()
    );

    const stats4Block = computed<BlockDef>(() =>
        createBlock<Stats4Props>({
            typeId: "stats4",
            type: "stats",
            name: t("page.blocks.stats.stats4"),
            component: stats4,
        })
            .withField(
                "backgroundImage",
                backgroundImageField(
                    translations.value.backgroundImage,
                    {
                        src: "https://id.easy.tools/assets/images/background.jpg",
                        transparency: 10,
                    },
                    {
                        src: translations.value.src,
                        transparency: translations.value.backgroundImageTransparency,
                    }
                )
            )
            .withField(
                "topLabel",
                headingField(translations.value.topLabel, "Our track record", "h3", {
                    text: translations.value.text,
                    tag: translations.value.tag,
                })
            )
            .withField(
                "title",
                headingField(
                    translations.value.title,
                    "Trusted by thousands of customers worldwide",
                    "h2",
                    {
                        text: translations.value.text,
                        tag: translations.value.tag,
                    }
                )
            )
            .withField(
                "subtitle",
                htmlField(
                    translations.value.subtitle,
                    "We're proud of our achievements and the trust our clients place in us",
                    translations.value.text
                )
            )
            .withArrayField(
                "stats",
                arrayField(
                    translations.value.stat,
                    "stats",
                    {
                        statTitle: { label: translations.value.title },
                        statDesc: { label: translations.value.description },
                    },
                    [
                        {
                            statTitle: "35K+",
                            statDesc: "Customers",
                        },
                        {
                            statTitle: "40+",
                            statDesc: "Countries",
                        },
                        {
                            statTitle: "500+",
                            statDesc: "Partners",
                        },
                        {
                            statTitle: "$25M",
                            statDesc: "Revenue",
                        },
                    ],
                    {
                        statTitle: "New stat",
                        statDesc: "New description",
                    }
                )
            )
            .withStyleFields([
                colorDefaults.value.background,
                colorDefaults.value.text,
                colorDefaults.value.border,
            ])
            .build()
    );

    const splitWithImageBlock = computed<BlockDef>(() =>
        createBlock<SplitWithImageProps>({
            typeId: "SplitWithImage",
            type: "stats",
            name: t("page.blocks.stats.splitWithImage"),
            component: SplitWithImage,
        })
            .withField(
                "tag",
                headingField(translations.value.tag, "Our performance", "h3", {
                    text: translations.value.text,
                    tag: translations.value.tag,
                })
            )
            .withField(
                "title",
                headingField(
                    translations.value.title,
                    "Global customer success and satisfaction metrics",
                    "h2",
                    {
                        text: translations.value.text,
                        tag: translations.value.tag,
                    }
                )
            )
            .withField(
                "description",
                htmlField(
                    translations.value.description,
                    "We equip businesses with tools for sustainable growth, combining advanced features and competitive pricing so you can focus on your customers.",
                    translations.value.text
                )
            )
            .withField(
                "image",
                descriptiveImageField(
                    translations.value.image,
                    {
                        src: getStockPhotoUrl(
                            "communities",
                            "diverse-friends-gathering-together.webp"
                        ),
                        alt: "Team collaborating in a contemporary office space",
                    },
                    {
                        src: translations.value.src,
                        alt: translations.value.alt,
                    }
                )
            )
            .withArrayField(
                "stats",
                arrayField(
                    translations.value.stat,
                    "stats",
                    {
                        statTitle: { label: translations.value.title },
                        statDesc: { label: translations.value.description },
                    },
                    [
                        {
                            statTitle: "12,500+",
                            statDesc: "Active users worldwide",
                        },
                        {
                            statTitle: "2.5%",
                            statDesc: "Service transaction fee",
                        },
                        {
                            statTitle: "99.95%",
                            statDesc: "Service availability",
                        },
                        {
                            statTitle: "$125M",
                            statDesc: "Revenue generated for users",
                        },
                    ],
                    {
                        statTitle: "New stat",
                        statDesc: "New description",
                    }
                )
            )
            .withStyleFields([
                colorDefaults.value.background,
                colorDefaults.value.text,
                colorDefaults.value.brand,
                colorDefaults.value.border,
            ])
            .build()
    );

    const steppedBlock = computed<BlockDef>(() =>
        createBlock<SteppedProps>({
            typeId: "stepped",
            type: "stats",
            name: t("page.blocks.stats.stepped"),
            component: stepped,
        })
            .withField(
                "title",
                headingField(
                    translations.value.title,
                    "Creating solutions that transform businesses",
                    "h2",
                    {
                        text: translations.value.text,
                        tag: translations.value.tag,
                    }
                )
            )
            .withField(
                "description",
                htmlField(
                    translations.value.description,
                    "We deliver innovative tools that help companies grow. Our platform combines powerful features with intuitive design for maximum impact.",
                    translations.value.text
                )
            )
            .withField("firstStat", {
                label: translations.value.firstStat,
                group: "stats",
                fields: [
                    {
                        value: "stat",
                        type: "text",
                        label: translations.value.stat,
                    },
                    {
                        value: "title",
                        type: "text",
                        label: translations.value.title,
                    },
                    {
                        value: "description",
                        type: "text",
                        label: translations.value.description,
                    },
                ],
                defaultData: {
                    data: {
                        stat: "350k",
                        title: "Users on the platform",
                        description:
                            "Growing community with active users across 45 countries worldwide.",
                    },
                    isHidden: false,
                },
            })
            .withField("secondStat", {
                label: translations.value.secondStat,
                group: "stats",
                fields: [
                    {
                        value: "stat",
                        type: "text",
                        label: translations.value.stat,
                    },
                    {
                        value: "title",
                        type: "text",
                        label: translations.value.title,
                    },
                    {
                        value: "description",
                        type: "text",
                        label: translations.value.description,
                    },
                ],
                defaultData: {
                    data: {
                        stat: "$7.2 billion",
                        title: "We're proud that our customers have made over $7 billion in total revenue.",
                        description:
                            "Achieved through strategic partnerships and innovative solutions.",
                    },
                    isHidden: false,
                },
            })
            .withField("thirdStat", {
                label: translations.value.thirdStat,
                group: "stats",
                fields: [
                    {
                        value: "stat",
                        type: "text",
                        label: translations.value.stat,
                    },
                    {
                        value: "title",
                        type: "text",
                        label: translations.value.title,
                    },
                    {
                        value: "description",
                        type: "text",
                        label: translations.value.description,
                    },
                ],
                defaultData: {
                    data: {
                        stat: "523,647",
                        title: "Transactions this year",
                        description:
                            "Secure and reliable processing with 99.9% uptime performance. Continuous improvement in our systems.",
                    },
                    isHidden: false,
                },
            })
            .withStyleFields([
                colorDefaults.value.background,
                colorDefaults.value.text,
                colorDefaults.value.brand,
            ])
            .build()
    );

    const timelineBlock = computed<BlockDef>(() =>
        createBlock<TimelineProps>({
            typeId: "timeline",
            type: "stats",
            name: t("page.blocks.stats.timeline"),
            component: timeline,
        })
            .withArrayField(
                "events",
                arrayField(
                    translations.value.event,
                    "events",
                    {
                        date: { label: translations.value.date },
                        title: { label: translations.value.title },
                        description: {
                            label: translations.value.description,
                            type: "html",
                        },
                    },
                    [
                        {
                            date: "Mar 2020",
                            title: "Initial concept developed",
                            description:
                                "We began with a groundbreaking idea and a dedicated group of innovators focused on developing transformative tools for companies across industries.",
                        },
                        {
                            date: "Sep 2020",
                            title: "Raised $42m in seed round",
                            description:
                                "Our innovative approach captured investor attention, providing resources to build our talented team and enhance our technology infrastructure.",
                        },
                        {
                            date: "Jun 2021",
                            title: "Launched alpha version",
                            description:
                                "We introduced our early platform to a select group of partners, collecting crucial insights that substantially improved our core offerings.",
                        },
                        {
                            date: "Apr 2022",
                            title: "Official market introduction",
                            description:
                                "After thorough development and refinement, we released our complete solution to the global market, empowering businesses worldwide.",
                        },
                    ],
                    {
                        date: "New date",
                        title: "New milestone",
                        description: "New milestone description",
                    }
                )
            )
            .withStyleFields([
                colorDefaults.value.background,
                colorDefaults.value.text,
                colorDefaults.value.brand,
                colorDefaults.value.border,
            ])
            .build()
    );

    return {
        blocks: computed(() => [
            stats1Block.value,
            stats2Block.value,
            stats3Block.value,
            stats4Block.value,
            splitWithImageBlock.value,
            steppedBlock.value,
            timelineBlock.value,
        ]),
    };
}
